import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { NeonEdge as NeonEdgeAtom } from '@sorare/core/src/atoms/ui/NeonEdge';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  mobileAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import background from '#src/assets/gems/background.webp';
import { GemsPack } from '#src/components/commonCard/BuyPacks/GemsPack/index.tsx';

import type { BuyGemsBlock_inGameCurrencyPurchasableItem } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  background: url(${background}) right center / cover no-repeat;
  border-radius: var(--triple-unit);
  overflow: hidden;
`;

const NeonEdge = styled(NeonEdgeAtom)`
  width: 100%;
`;
const Main = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Header = styled(Horizontal).attrs({ center: true })`
  padding-bottom: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
`;
const GemsPackContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(115px, 1fr));

  @media ${mobileAndAbove} {
    grid-template-columns: repeat(3, minmax(115px, 1fr));
  }
  column-gap: var(--unit);
  row-gap: var(--double-unit);
`;

type Props = {
  gemsPacks: BuyGemsBlock_inGameCurrencyPurchasableItem[];
};
export const BuyGemsBlock = ({ gemsPacks }: Props) => {
  const sortedGemsPacks = [...(gemsPacks || [])].sort((a, b) => {
    const aAmount = a.baseAmount;
    const bAmount = b.baseAmount;
    return aAmount - bAmount;
  });

  return (
    <Root>
      <NeonEdge side="top" showNeon="always" color="#8CE456">
        <Main>
          <Header>
            <BrandL bold>
              <FormattedMessage {...glossary.buyGems} />
            </BrandL>
          </Header>
          <GemsPackContainer>
            {sortedGemsPacks.map(gemsPack => (
              <GemsPack key={gemsPack.id} gemsPack={gemsPack} />
            ))}
          </GemsPackContainer>
        </Main>
      </NeonEdge>
    </Root>
  );
};

BuyGemsBlock.fragments = {
  inGameCurrencyPurchasableItem: gql`
    fragment BuyGemsBlock_inGameCurrencyPurchasableItem on InGameCurrencyPurchasableItem {
      slug
      baseAmount
      ...GemsPack_inGameCurrencyPurchasableItem
    }
    ${GemsPack.fragments.inGameCurrencyPurchasableItem}
  ` as TypedDocumentNode<BuyGemsBlock_inGameCurrencyPurchasableItem>,
};
